import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { EndOutcome, GAME_TYPES } from '@/features/gameSession/contracts';
import { getElapsedSeconds } from '@/shared/utils/time';
import { Analytics } from '../utils/analytics';
import { MeasurementGameScreen, WORLD_KEYS } from '../types';
import { createWorldSequence } from '../utils/worldSequence';
import { getMeasurementLevels } from '../utils/levels';

import type { GameAnswerEvent, GameCloseEvent, GameData, GameFinishEvent } from '@/features/gameSession';
import type { GameplayEvent, MeasurementGameEvent } from '../utils/analytics';
import type { MeasurementGameData, MeasurementGameLevelProgress, NpcSession } from '../types';

export type SessionOptions = {
	autostart?: boolean;
	debugStartLevelIndex?: number;
	gameData?: MeasurementGameData | null;
	levelProgress?: MeasurementGameLevelProgress | null;
	onStart?: () => void;
	onEvent?: (event: MeasurementGameEvent) => void;
	onAnswer?: (event: GameAnswerEvent) => void;
	onFinish?: (event: GameFinishEvent) => void;
	onReplay?: (gameData: GameData) => void;
	onClose?: (event?: GameCloseEvent) => void;
};

const LEVEL_REWARD_AMOUNT = 5;
const MIN_COMPLETED_LEVELS = 3;
const MIN_SESSION_DURATION_MS = 3 * 60 * 1000;

export function useGameSession({
	autostart = false,
	debugStartLevelIndex,
	gameData,
	levelProgress,
	onAnswer,
	onClose,
	onFinish,
	onReplay,
	onStart,
	onEvent,
}: SessionOptions) {
	const questions = gameData?.questions;
	const levels = useMemo(() => getMeasurementLevels({ questions }), [questions]);
	const debugLevelSelected =
		typeof debugStartLevelIndex === 'number' &&
		Number.isInteger(debugStartLevelIndex) &&
		debugStartLevelIndex >= 0 &&
		debugStartLevelIndex < levels.length;
	const startLevelIndex = debugLevelSelected ? debugStartLevelIndex : 0;
	const startImmediately = autostart || debugLevelSelected;

	const [activeScreen, setActiveScreen] = useState(
		startImmediately ? MeasurementGameScreen.Game : MeasurementGameScreen.Start,
	);
	const [levelIndex, setLevelIndex] = useState(startLevelIndex);
	const [gameLoading, setGameLoading] = useState(true);

	const initialAmount = gameData?.game?.total_score ?? 0;

	const [totalAmount, setTotalAmount] = useState(initialAmount);
	const [outcome, setOutcome] = useState(EndOutcome.LOST);

	const questionStartedAtRef = useRef<number | null>(startImmediately ? Date.now() : null);
	const sessionStartedAtRef = useRef(questionStartedAtRef.current);
	const npcSessionRef = useRef<NpcSession>({ encounterSeen: false, factShown: false });
	const attemptNumberRef = useRef(0);
	const eventHandlerRef = useRef(onEvent);
	eventHandlerRef.current = onEvent;
	const analyticsRef = useRef<Analytics | null>(null);
	if (!analyticsRef.current) {
		analyticsRef.current = new Analytics((event) => eventHandlerRef.current?.(event));
		if (startImmediately) analyticsRef.current.start(sessionStartedAtRef.current ?? Date.now());
	}
	const analytics = analyticsRef.current;
	const lifecycleRef = useRef(0);

	const worlds = useMemo(
		() => createWorldSequence(levels.length, gameData?.firstWorld),
		[gameData?.firstWorld, levels.length],
	);

	const level = levels[levelIndex];
	const world = level?.world ?? worlds[levelIndex] ?? WORLD_KEYS[0];

	useEffect(() => setTotalAmount(initialAmount), [initialAmount]);

	useEffect(() => {
		if (activeScreen === MeasurementGameScreen.Game && level) {
			analytics.startLevel(levelIndex, level.name, level.id, gameData?.id ?? 0);
		}
	}, [activeScreen, analytics, gameData?.id, level, levelIndex]);

	useEffect(() => {
		const lifecycle = ++lifecycleRef.current;
		const handlePageHide = () => analytics.abandon();
		window.addEventListener('pagehide', handlePageHide);
		return () => {
			window.removeEventListener('pagehide', handlePageHide);
			// React Strict Mode immediately reconnects effects; only abandon a real unmount.
			queueMicrotask(() => {
				if (lifecycleRef.current === lifecycle) analytics.abandon();
			});
		};
	}, [analytics]);

	const handleGameplayEvent = useCallback((event: GameplayEvent) => analytics.gameplay(event), [analytics]);

	const handleStart = useCallback(() => {
		questionStartedAtRef.current = Date.now();
		analytics.start(questionStartedAtRef.current);
		sessionStartedAtRef.current = questionStartedAtRef.current;
		attemptNumberRef.current = 0;
		npcSessionRef.current = { encounterSeen: false, factShown: false };

		setLevelIndex(startLevelIndex);
		setGameLoading(true);
		setActiveScreen(MeasurementGameScreen.Game);

		onStart?.();
	}, [analytics, onStart, startLevelIndex]);

	const handleReplay = useCallback(() => {
		const nextGameData = levelProgress?.user_learning_path_section;
		const replayAmount = nextGameData?.game?.total_score ?? initialAmount;

		if (nextGameData) {
			onReplay?.({
				...nextGameData,
				game: {
					...gameData?.game,
					...nextGameData.game,
					type: nextGameData.game?.type ?? gameData?.game?.type ?? GAME_TYPES.MEASUREMENT_GAME,
					total_score: replayAmount,
				},
			});
		}

		setTotalAmount(replayAmount);
		if (debugLevelSelected) handleStart();
		else setActiveScreen(MeasurementGameScreen.Start);
	}, [debugLevelSelected, gameData?.game, handleStart, initialAmount, levelProgress, onReplay]);

	const handleBack = useCallback(() => {
		analytics.abandon();
		if (onClose) {
			onClose();
			return;
		}

		setActiveScreen(MeasurementGameScreen.Start);
	}, [analytics, onClose]);

	const handleAnswer = useCallback(
		(answerText: string, isCorrect: boolean) => {
			const questionIndex = level?.questionIndex ?? -1;
			const question = gameData?.questions?.[questionIndex];

			if (!question) return;

			attemptNumberRef.current += 1;
			analytics.answer();

			onAnswer?.({
				gameType: GAME_TYPES.MEASUREMENT_GAME,
				question: {
					...question,
					ai_tutor_enabled: question.ai_tutor_enabled ?? false,
					explanation_available: question.explanation_available ?? false,
					time_estimate_in_seconds: question.time_estimate_in_seconds ?? 0,
					title: question.title ?? '',
					topic_id: question.topic_id ?? 0,
					topic_title: question.topic_title ?? '',
				},
				questionIndex,
				isCorrect,
				answerId: question.answers.find((answer) => answer?.is_correct)?.id,
				answerTexts: [answerText],
				answerTimeInSeconds: getElapsedSeconds(questionStartedAtRef.current),
				attemptNumber: attemptNumberRef.current,
				isSecondAttempt: attemptNumberRef.current > 1,
			});
		},
		[analytics, gameData, level, onAnswer],
	);

	const handleFinish = useCallback(
		(event: GameFinishEvent) => {
			const succeeded = event.gameOutcome === EndOutcome.SUCCEEDED;

			if (succeeded) {
				analytics.complete(gameData?.id ?? 0, LEVEL_REWARD_AMOUNT);
				setTotalAmount((amount) => amount + LEVEL_REWARD_AMOUNT);
			}

			const completionRequirementsMet =
				!debugLevelSelected &&
				levelIndex + 1 >= MIN_COMPLETED_LEVELS &&
				sessionStartedAtRef.current !== null &&
				Date.now() - sessionStartedAtRef.current >= MIN_SESSION_DURATION_MS;

			if (succeeded && !completionRequirementsMet && levelIndex < levels.length - 1) {
				questionStartedAtRef.current = Date.now();
				attemptNumberRef.current = 0;
				setLevelIndex(levelIndex + 1);
				setGameLoading(true);
				return;
			}

			analytics.finish();
			setActiveScreen(MeasurementGameScreen.End);
			setOutcome(event.gameOutcome);

			onFinish?.({ ...event, score: totalAmount + (succeeded ? LEVEL_REWARD_AMOUNT : 0) });
		},
		[analytics, debugLevelSelected, gameData?.id, levelIndex, levels.length, onFinish, totalAmount],
	);

	const handleCoinCollect = useCallback((value: number) => setTotalAmount((amount) => amount + value), []);

	return {
		activeScreen,
		gameLoading,
		totalAmount,
		outcome,
		npcSessionRef,
		level,
		world,
		introMessages: levelIndex === 0 ? gameData?.game?.messages?.intro ?? undefined : undefined,
		onStart: handleStart,
		onReplay: handleReplay,
		onBack: handleBack,
		onAnswer: handleAnswer,
		onFinish: handleFinish,
		onCoinCollect: handleCoinCollect,
		onLoadingChange: setGameLoading,
		onGameplayEvent: handleGameplayEvent,
	};
}
